import React, { useState } from "react";
import { toast } from "sonner";
import { startMembershipCheckout } from "../AcademyWaitlistLock";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Check, Crown, Star } from "lucide-react";

const TIERS = [
  {
    id: "academy", label: "Technical Track", price: "$29/mo", icon: Star, accent: "#C41E24",
    perks: ["Unlimited video reviews", "Personal voice-note feedback on every clip"],
  },
  {
    id: "elite", label: "Elite Coaching", price: "$49/mo", icon: Crown, accent: "#D4AF37",
    perks: ["Everything in the Technical Track", "Priority leaderboard ranking", "Personal coach videos, just for you"],
  },
];

export default function UnlockModal({ open, feature, player, email, tier, eliteOnly, featureTitle, onClose }) {
  const [joining, setJoining] = useState(null);
  const eliteUpsell = eliteOnly && tier === "academy";
  const visibleTiers = eliteUpsell ? TIERS.filter((t) => t.id === "elite") : TIERS;

  const join = async (tierId) => {
    setJoining(tierId);
    try { await startMembershipCheckout(player, tierId); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't start checkout. Try again."); setJoining(null); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="unlock-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
          onClick={onClose}>
          <motion.div
            initial={{ scale: 0.94, y: 18 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 18 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#0f0f10] p-6 sm:p-7 overflow-hidden">
            <button data-testid="unlock-modal-close" onClick={onClose} aria-label="Close"
              className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-sm text-white/40 hover:text-white transition-colors"><X size={18} /></button>
            <div className="flex items-center gap-2 mb-1">
              <span className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: eliteUpsell ? "#D4AF3722" : "#C41E2433" }}>
                {eliteUpsell ? <Crown size={15} style={{ color: "#D4AF37" }} /> : <Lock size={15} className="text-[#C41E24]" />}
              </span>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: eliteUpsell ? "#D4AF37" : "#C41E24" }}>{eliteUpsell ? "Elite Only" : "Locked Feature"}</div>
            </div>
            <h2 className="font-headings text-2xl uppercase tracking-wide mb-1.5">{eliteUpsell ? "Upgrade to Elite" : "Want to unlock this feature?"}</h2>
            <p className="text-sm text-white/60 mb-5">
              {eliteUpsell
                ? <><b className="text-white/80">{featureTitle || "This feature"}</b> is an Elite-only perk. Upgrade to Elite Coaching to unlock your custom training roadmap.</>
                : <>Enrollment is <b className="text-white/80">open now</b> — pick the membership that fits and you're in today. Founding Member pricing is locked for life.</>}
            </p>

            <div className={`grid grid-cols-1 gap-3 ${eliteUpsell ? "" : "sm:grid-cols-2"}`}>
              {visibleTiers.map((t) => (
                <div key={t.id} data-testid={`unlock-tier-${t.id}`}
                  className="rounded-xl border p-4 flex flex-col" style={{ borderColor: `${t.accent}55`, background: `${t.accent}0d` }}>
                  <div className="flex items-center gap-2 mb-2">
                    <t.icon size={16} style={{ color: t.accent }} />
                    <span className="font-subheadings font-bold uppercase tracking-wide text-sm">{t.label}</span>
                  </div>
                  <div className="font-cover text-2xl mb-3" style={{ color: t.accent }}>{t.price}</div>
                  <ul className="space-y-1.5 mb-4 flex-1">
                    {t.perks.map((p) => (
                      <li key={p} className="flex items-start gap-1.5 text-xs text-white/70">
                        <Check size={13} style={{ color: t.accent }} className="shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                  <button data-testid={`unlock-join-${t.id}`} onClick={() => join(t.id)} disabled={joining === t.id}
                    className="w-full text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60"
                    style={{ background: t.accent, color: t.id === "elite" ? "#111" : "#fff" }}>
                    {joining === t.id ? "Redirecting…" : `Enroll · ${t.price}`}
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
